// components/StudentGoalsList.js
import React, { useEffect, useState } from "react";
import { getRequest, putRequest, deleteRequest } from "../../apiWrapper";
import { FaChevronDown, FaStar, FaRegStar } from "react-icons/fa";
import { toast } from "react-toastify";
import debounce from 'lodash.debounce';
import { useCallback } from 'react';
import AddStudentGoalModal from "./AddStudentGoalModal";
import ExportGoalsModal from "./ExportGoalsModal";

const statusLabels = {
  not_started: "未着手",
  not_understood: "理解不十分",
  needs_explanation: "解説必要",
  can_answer_alone: "独力解答",
  mastered: "修得",
  in_progress: "練習中",
  completed: "完了"
};

const statusColors = {
  not_started: "bg-gray-300",
  not_understood: "bg-red-300",
  needs_explanation: "bg-yellow-300",
  can_answer_alone: "bg-blue-300",
  mastered: "bg-green-500",
  in_progress: "bg-blue-300",
  completed: "bg-green-500"
};

const statusOrder = {
  not_started: 0,
  not_understood: 1,
  needs_explanation: 2,
  in_progress: 3,
  can_answer_alone: 4,
  mastered: 5,
  completed: 6
};

const statusOptionsMap = {
  task: [
    "not_started",
    "not_understood",
    "needs_explanation",
    "can_answer_alone",
    "mastered"
  ],
  test: [
    "in_progress",
    "completed"
  ]
};

const StudentGoalsList = ({ studentid }) => {
  const [goals, setGoals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("studentGoalsSort") || "status");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [isSaving, setIsSaving] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);


  const userRole = sessionStorage.getItem("userRole");
  const currentTeacherId = sessionStorage.getItem("teacherid");
  const isAdmin = userRole === "admin";

  useEffect(() => {
    localStorage.setItem("studentGoalsSort", sortMode);
  }, [sortMode]);

  useEffect(() => {
    localStorage.setItem("studentGoalsSubject", selectedSubject);
  }, [selectedSubject]);

  useEffect(() => {
    const fetchGoals = async () => {
      if (!studentid) return;
      try {
        const data = await getRequest(`student_goals?studentid=${studentid}`);
        if (data?.goals) setGoals(data.goals);
      } catch (error) {
        console.error("❌ 課題取得エラー:", error);
      }
    };
    fetchGoals();
  }, [studentid]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const subjectData = await getRequest("subjects");
        if (!subjectData?.subjects) return;

        setAllSubjects(subjectData.subjects);

        if (isAdmin) {
          setSubjects([{ id: "all", name: "全科目" }, ...subjectData.subjects]);
          setSelectedSubject(localStorage.getItem("studentGoalsSubject") || "all");
        } else {
          const assignData = await getRequest("teacherAssignments");
          const teacherSubs = assignData?.students?.[studentid]?.subjects || [];
          const filtered = subjectData.subjects.filter((s) => teacherSubs.includes(s.name));
          setSubjects(filtered);
          if (filtered.length > 0) {
            setSelectedSubject(filtered[0].id);
          }
        }
      } catch (error) {
        console.error("❌ 科目取得エラー:", error);
      }
    };
    fetchSubjects();
  }, [studentid, isAdmin]);

  const canEditGoal = (goal) => {
    return userRole === "admin" || goal.created_by === currentTeacherId;
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((gid) => gid !== id) : [...prev, id]
    );
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const utcDate = new Date(dateStr);
    const jstDate = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000);
    const year = jstDate.getFullYear();
    const month = String(jstDate.getMonth() + 1).padStart(2, "0");
    const day = String(jstDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // input[type="date"]向け
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return "--/--";
    const utcDate = new Date(dateStr);
    const jstDate = new Date(utcDate.getTime() + 9 * 60 * 60 * 1000);
    const month = String(jstDate.getMonth() + 1).padStart(2, "0");
    const day = String(jstDate.getDate()).padStart(2, "0");
    return `${month}/${day}`; // 表示用（mm/dd）
  };

  const handleFieldChange = (id, field, value) => {
    setGoals((prev) =>
      prev.map((g) => {
        if (g.id !== id) return g;
        const updated = { ...g, [field]: value };

        if (field === "created_at") {
          const jstDate = value + "T00:00:00+09:00";
          updated.created_at_jst = jstDate;
        } else if (field === "due_date") {
          // そのままJST表現の文字列として保持
          const jstDate = value + "T00:00:00+09:00";
          updated.due_date_jst = jstDate;
        }

        debouncedSaveGoal(updated); // リアルタイム保存

        return updated;
      })
    );
  };

  const saveGoal = async (goal) => {
    setIsSaving(true);
    try {
      const res = await putRequest(`student_goals/${goal.id}`, {
        title: goal.title,
        status: goal.status,
        priority: goal.priority,
        memo: goal.memo || "",
        created_at: goal.created_at_jst?.split("T")[0], // UTC変換しない
        due_date: goal.due_date_jst?.split("T")[0], // UTC変換しない
        required_level: goal.required_level
      });

      if (!res.ok) {
        toast.error("保存に失敗しました");
      }
    } catch (err) {
      console.error("❌ 自動保存エラー:", err);
      toast.error("保存に失敗しました");
    } finally {
      setTimeout(() => setIsSaving(false), 1000);
    }
  };

  const debouncedSaveGoal = useCallback(debounce(saveGoal, 1200), []);

  const deleteGoal = async (goalId, title) => {
    if (!window.confirm(`「${title}」を削除してもよろしいですか？`)) return;
    try {
      const res = await deleteRequest(`student_goals/${goalId}`);
      if (res.ok) {
        setGoals((prev) => prev.filter((g) => g.id !== goalId));
        toast.success("課題を削除しました");
      } else {
        toast.error("削除に失敗しました");
      }
    } catch (error) {
      console.error("❌ 削除エラー:", error);
      toast.error("削除に失敗しました");
    }
  };

  const filteredGoals = goals.filter((g) => selectedSubject === "all" || g.subjectid === selectedSubject);

  const sortedGoals = [...filteredGoals]
    .filter(g => g.status !== "completed" && g.status !== "mastered")
    .sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const pComp = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (pComp !== 0) return pComp;

      if (sortMode === "due") {
        const dateA = a.due_date_jst || "";
        const dateB = b.due_date_jst || "";
        if (dateA !== dateB) return dateA.localeCompare(dateB);
      } else if (sortMode === "status") {
        const sDiff = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
        if (sDiff !== 0) return sDiff;
      }

      return (a.title || '').localeCompare(b.title || '');
    });

  const completedGoals = [...filteredGoals]
    .filter(g => g.status === "completed" || g.status === "mastered")
    .sort((a, b) => new Date(a.created_at_jst) - new Date(b.created_at_jst));

  const handleAddGoal = (newGoal) => {
    setGoals(prev => [...prev, newGoal]);
  };

  const renderSubjectButtons = () => (
    <div className="mb-4 flex gap-2 text-sm flex-wrap">
      {subjects.map((subj) => (
        <button
          key={subj.id}
          className={`px-3 py-1 border rounded ${selectedSubject === subj.id ? 'bg-blue-500 text-white' : 'bg-white'}`}
          onClick={() => setSelectedSubject(subj.id)}
        >
          {subj.name}
        </button>
      ))}
    </div>
  );

  const renderGoalCard = (goal) => {
    const statusColor = statusColors[goal.status] || "bg-gray-200";
    const statusOptions = statusOptionsMap[goal.type] || [];
    const editable = canEditGoal(goal);

    return (
      <div key={goal.id} className="border rounded shadow-sm bg-white transition overflow-hidden">
        <div
          className="flex justify-between items-center p-3 hover:bg-gray-50 cursor-pointer"
          onClick={() => toggleExpand(goal.id)}
        >
          <div className="flex justify-between items-center w-full pr-6">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-3 h-3 rounded-full ${statusColor}`} title={statusLabels[goal.status]}></div>
              <span
                className={`truncate ${goal.status === "completed" || goal.status === "mastered"
                  ? "text-gray-500 line-through font-normal"
                  : "font-semibold text-black"
                  }`}
              >
                {goal.title}
              </span>

            </div>
            <div className="flex-shrink-0 flex gap-3 text-xs text-right min-w-[130px] justify-end">
              {editable ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // カード展開防止
                    handleFieldChange(goal.id, "priority", goal.priority === "high" ? "low" : "high");
                  }}
                  className="text-lg"
                  title={goal.priority === "high" ? "優先を解除" : "優先する"}
                >
                  {goal.priority === "high" ? (
                    <FaStar className="text-yellow-500" />
                  ) : (
                    <FaRegStar className="text-gray-400" />
                  )}
                </button>
              ) : (
                goal.priority === "high" ? (
                  <FaStar className="text-yellow-500 text-lg" />
                ) : (
                  <FaRegStar className="text-gray-400 text-lg" />
                )
              )}
              <span className="text-gray-500 min-w-[70px] text-left block">
                期限：{formatShortDate(goal.due_date_jst)}
              </span>
            </div>
          </div>
          <FaChevronDown className={`transition ${expandedIds.includes(goal.id) ? "rotate-180" : "rotate-0"}`} />
        </div>

        {expandedIds.includes(goal.id) && (
          <div className="border-t px-4 py-3 text-sm text-gray-700 bg-gray-50">
            <div className="mb-3">
              <label className="block font-semibold mb-1">【課題タイトル】</label>
              {editable ? (
                <input
                  type="text"
                  value={goal.title}
                  onChange={(e) => handleFieldChange(goal.id, "title", e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                />
              ) : (
                <span className="text-sm">{goal.title}</span>
              )}
            </div>

            <ul className="mb-2 space-y-1">
              <li>
                - 登録日：{editable && userRole === "admin" ? (
                  <input
                    type="date"
                    value={formatDateForInput(goal.created_at_jst)}
                    onChange={(e) => handleFieldChange(goal.id, "created_at", e.target.value)}
                    className="border px-2 py-1 rounded text-sm"
                  />
                ) : formatShortDate(goal.created_at_jst)}
              </li>
              <li>
                - 期限：{editable ? (
                  <input
                    type="date"
                    value={formatDateForInput(goal.due_date_jst)}
                    onChange={(e) => handleFieldChange(goal.id, "due_date", e.target.value)}
                    className="border px-2 py-1 rounded text-sm"
                  />
                ) : formatShortDate(goal.due_date_jst)}
              </li>
              {editable && goal.type === "test" && (
                <li>
                  - 合格ライン：<input
                    type="number"
                    value={goal.required_level || ""}
                    onChange={(e) => handleFieldChange(goal.id, "required_level", e.target.value)}
                    className="w-20 px-2 py-1 border rounded text-sm"
                    min="0"
                    max="100"
                    step="10"
                  />％
                </li>
              )}
            </ul>

            <div className="mb-3">
              <span className="block font-semibold mb-1">【ステータス選択】</span>
              <div className="flex gap-3 flex-wrap">
                {statusOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => handleFieldChange(goal.id, "status", opt)}
                    className={`px-3 py-1 border rounded-full text-sm ${goal.status === opt ? "bg-blue-500 text-white" : "bg-white"}`}
                  >
                    {statusLabels[opt]}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3">
              <span className="block font-semibold mb-1">【メモ】</span>
              <textarea
                value={goal.memo || ""}
                onChange={(e) => handleFieldChange(goal.id, "memo", e.target.value)}
                className="w-full p-2 border rounded text-sm"
                rows={3}
                placeholder="メモを入力..."
              ></textarea>
            </div>

            <div className="flex gap-4">
              {isAdmin && (
                <button
                  onClick={() => deleteGoal(goal.id, goal.title)}
                  className="px-4 py-2 bg-red-500 text-white rounded shadow hover:bg-red-600"
                >
                  削除
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {isSaving && (
        <div className="fixed top-0 left-0 w-full h-[3px] z-50 bg-transparent overflow-hidden p-0 m-0">
          <div className="h-full w-1/3 bg-blue-500 animate-loading-slide p-0 m-0"></div>
        </div>
      )}

      {renderSubjectButtons()}

      <div className="mb-4 flex gap-2 text-sm">
        <button
          className={`px-3 py-1 border rounded ${sortMode === 'status' ? 'bg-blue-500 text-white' : 'bg-white'}`}
          onClick={() => setSortMode("status")}
        >
          進行度が低い順
        </button>
        <button
          className={`px-3 py-1 border rounded ${sortMode === 'due' ? 'bg-blue-500 text-white' : 'bg-white'}`}
          onClick={() => setSortMode("due")}
        >
          期限が近い順
        </button>
      </div>

      {isAdmin && (
        <div className="mb-4 flex gap-2 text-sm flex-wrap">
          <button
            className="px-3 py-1 border rounded bg-green-500 text-white hover:bg-green-600"
            onClick={() => setShowExportModal(true)}
          >
            Excel出力
          </button>
        </div>
      )}


      {sortedGoals.length === 0 && <p className="text-gray-500">未完了の課題はありません。</p>}
      <div className="space-y-3">
        {sortedGoals.map(renderGoalCard)}
      </div>

      {completedGoals.length > 0 && (
        <div className="mt-10 pt-6 border-t">
          <h3 className="text-xl font-bold mb-3 text-gray-700">完了した課題</h3>
          <div className="space-y-3">
            {completedGoals.map(renderGoalCard)}
          </div>
        </div>
      )}

      {(isAdmin || sessionStorage.getItem("teacherid")) && (
        <button
          className="fixed bottom-6 right-6 bg-blue-500 hover:bg-blue-600 text-white text-2xl rounded-full w-14 h-14 shadow-lg z-50"
          onClick={() => setShowModal(true)}
        >
          ＋
        </button>
      )}

      {showModal && (
        <AddStudentGoalModal
          studentid={studentid}
          onClose={() => setShowModal(false)}
          onAdded={handleAddGoal}
          isAdmin={isAdmin}
        />
      )}

      {showExportModal && (
        <ExportGoalsModal
          studentid={studentid}
          onClose={() => setShowExportModal(false)}
        />
      )}

    </div>
  );
};

export default StudentGoalsList;
